import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  LinearProgress,
  Badge,
  Container,
  AppBar,
  Toolbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar
} from '@mui/material';
import {
  LiveTv as LiveIcon,
  PlayArrow as PlayIcon,
  People as PeopleIcon,
  Timer as TimerIcon,
  Schedule as ScheduleIcon,
  Refresh as RefreshIcon,
  ArrowBack as BackIcon,
  EmojiEvents as TrophyIcon,
  Lock as LockIcon,
  AccessTime as UpcomingIcon,
  Leaderboard as LeaderboardIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import { useLiveQuizUser } from '../../../hooks/useLiveQuiz';
import { useAuthContext } from '../../../../context/AuthContext';
import UserAuthButton from '../../auth/UserAuthButton';
import AuthModal from '../../auth/AuthModal';
import { getGuestDisplayName } from '../../../../utils/guestDisplayUtils';

// Styled components
const WelcomeContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#121212',
  minHeight: '100vh',
  padding: '24px',
}));

const StyledCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#1A1A1A',
  border: '1px solid #333',
  borderRadius: '16px',
  marginBottom: '24px',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: '#4CAF50',
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 32px rgba(76, 175, 80, 0.15)',
  },
}));

const ActionButton = styled(Button)(({ theme, status }) => ({
  backgroundColor: status === 'active' ? '#4CAF50' : 
                   status === 'upcoming' ? '#2196F3' :
                   '#9E9E9E',
  color: '#fff',
  borderRadius: '12px',
  textTransform: 'none',
  fontWeight: 600,
  padding: '12px 24px',
  fontSize: '1.1rem',
  '&:hover': {
    backgroundColor: status === 'active' ? '#45a049' : 
                     status === 'upcoming' ? '#1976D2' :
                     '#757575',
    transform: status === 'active' ? 'translateY(-2px)' : 'none',
    boxShadow: status === 'active' ? '0 8px 24px rgba(76, 175, 80, 0.3)' : 'none',
  },
  '&:disabled': {
    backgroundColor: status === 'upcoming' ? 'rgba(33, 150, 243, 0.3)' : 
                    'rgba(158, 158, 158, 0.3)',
    color: status === 'upcoming' ? 'rgba(255, 255, 255, 0.7)' : 
           'rgba(255, 255, 255, 0.5)',
    cursor: 'not-allowed',
  },
  '&.MuiButton-outlined': {
    borderColor: status === 'active' ? '#4CAF50' : 
                 status === 'upcoming' ? '#2196F3' :
                 '#9E9E9E',
    color: status === 'active' ? '#4CAF50' : 
           status === 'upcoming' ? '#2196F3' :
           '#9E9E9E',
    '&:hover': {
      backgroundColor: status === 'active' ? 'rgba(76, 175, 80, 0.1)' : 
                       status === 'upcoming' ? 'rgba(33, 150, 243, 0.1)' :
                       'rgba(158, 158, 158, 0.1)',
    },
  },
}));

const StatusChip = styled(Chip)(({ theme, status }) => ({
  backgroundColor: status === 'active' ? 'rgba(76, 175, 80, 0.2)' : 
                   status === 'upcoming' ? 'rgba(33, 150, 243, 0.2)' :
                   'rgba(158, 158, 158, 0.2)',
  color: status === 'active' ? '#4CAF50' : 
         status === 'upcoming' ? '#2196F3' :
         '#9E9E9E',
  border: `1px solid ${status === 'active' ? '#4CAF50' : 
                      status === 'upcoming' ? '#2196F3' :
                      '#9E9E9E'}`,
  fontWeight: 600,
  fontSize: '0.8rem',
}));

const LiveQuizWelcome = ({ onBack, onJoinQuiz }) => {
  const [refreshing, setRefreshing] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [userParticipations, setUserParticipations] = useState({}); // { [sessionId]: 'active' | 'completed' }
  const { enqueueSnackbar } = useSnackbar();
  const { user, isAuthenticated } = useAuthContext();

  const {
    activeSessions,
    loading,
    error,
    joinSession
  } = useLiveQuizUser(false); // Disable auto-refresh to prevent modal closing

  // State for all sessions (active and inactive)
  const [allSessions, setAllSessions] = useState([]);
  const [allSessionsLoading, setAllSessionsLoading] = useState(false);
  const [allSessionsError, setAllSessionsError] = useState(null);
  
  // State for leaderboards
  const [leaderboards, setLeaderboards] = useState({}); // { [sessionId]: leaderboardData }
  const [leaderboardLoading, setLeaderboardLoading] = useState({}); // { [sessionId]: boolean }
  const [expandedLeaderboards, setExpandedLeaderboards] = useState({}); // { [sessionId]: boolean }

  // Determine session status based on current time and session data
  const getSessionStatus = useCallback((session) => {
    const now = new Date();
    const scheduledStart = session.scheduled_start_time ? new Date(session.scheduled_start_time) : null;
    const actualStart = session.actual_start_time ? new Date(session.actual_start_time) : null;
    const actualEnd = session.actual_end_time ? new Date(session.actual_end_time) : null;
    
    // If session has ended
    if (actualEnd) {
      return 'expired';
    }
    
    // If session has actually started
    if (actualStart) {
      return 'active';
    }
    
    // If session is scheduled for future
    if (scheduledStart && scheduledStart > now) {
      return 'upcoming';
    }
    
    // If session is scheduled but time has passed and no actual start
    if (scheduledStart && scheduledStart <= now) {
      return 'expired';
    }
    
    // Default to database status
    return session.status || 'inactive';
  }, []);

  // Format time until start for upcoming sessions
  const getTimeUntilStart = useCallback((scheduledStartTime) => {
    if (!scheduledStartTime) return null;
    
    const now = new Date();
    const startTime = new Date(scheduledStartTime);
    const diffMs = startTime.getTime() - now.getTime();
    
    if (diffMs <= 0) return null;
    
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 0) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''}`;
    } else if (diffHours > 0) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''}`;
    } else {
      return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''}`;
    }
  }, []);

  // Fetch leaderboard for a specific session
  const fetchLeaderboard = useCallback(async (sessionId) => {
    try {
      setLeaderboardLoading(prev => ({ ...prev, [sessionId]: true }));
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/live-quiz/sessions/${sessionId}/leaderboard`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch leaderboard');
      }
      
      const data = await response.json();
      setLeaderboards(prev => ({ ...prev, [sessionId]: data.leaderboard || [] }));
    } catch (err) {
      console.error('Error fetching leaderboard:', err);
      setLeaderboards(prev => ({ ...prev, [sessionId]: [] }));
    } finally {
      setLeaderboardLoading(prev => ({ ...prev, [sessionId]: false }));
    }
  }, []);

  // Toggle leaderboard expansion and fetch data if needed
  const toggleLeaderboard = useCallback(async (sessionId) => {
    const isExpanded = expandedLeaderboards[sessionId];
    
    if (!isExpanded && !leaderboards[sessionId]) {
      // Fetch leaderboard data when expanding for the first time
      await fetchLeaderboard(sessionId);
    }
    
    setExpandedLeaderboards(prev => ({
      ...prev,
      [sessionId]: !isExpanded
    }));
  }, [expandedLeaderboards, leaderboards, fetchLeaderboard]);

  // Fetch all sessions (active and inactive)
  const fetchAllSessions = useCallback(async () => {
    try {
      setAllSessionsLoading(true);
      setAllSessionsError(null);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/live-quiz/sessions`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch all sessions');
      }
      
      const data = await response.json();
      // Add computed status to each session
      const sessionsWithStatus = (data.sessions || []).map(session => ({
        ...session,
        computedStatus: getSessionStatus(session)
      }));
      setAllSessions(sessionsWithStatus);
    } catch (err) {
      setAllSessionsError(err.message);
    } finally {
      setAllSessionsLoading(false);
    }
  }, [getSessionStatus]);

  // Check if user is participating in a session
  const checkUserParticipation = useCallback(async (sessionId) => {
    if (!isAuthenticated || !user) return false;
    
    try {
      const deviceId = localStorage.getItem('device_id');
      const userId = user.user_type === 'registered' ? user.id : null;
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/live-quiz/sessions/${sessionId}/participants`);
      if (!response.ok) return false;
      
      const data = await response.json();
      const participants = data.participants || [];
      
      // Find this user's participation entry
      const entry = participants.find(participant => 
        (userId && participant.user_id === userId) || 
        (deviceId && participant.device_id === deviceId)
      );
      if (!entry) return false;
      return entry.status === 'completed' ? 'completed' : 'active';
    } catch (error) {
      console.error('Error checking user participation:', error);
      return false;
    }
  }, [isAuthenticated, user]);

  // Check participation for all sessions
  const checkAllParticipations = useCallback(async () => {
    if (!isAuthenticated || !user) return;
    
    const participations = {};
    for (const session of allSessions) {
      participations[session.session_id] = await checkUserParticipation(session.session_id);
    }
    setUserParticipations(participations);
  }, [isAuthenticated, user, allSessions, checkUserParticipation]);

  // Manual refresh on component mount
  useEffect(() => {
    fetchAllSessions();
  }, [fetchAllSessions]);

  // Check participations when sessions or auth state changes
  useEffect(() => {
    if (allSessions.length > 0 && isAuthenticated && user) {
      checkAllParticipations();
    }
  }, [allSessions, isAuthenticated, user, checkAllParticipations]);

  // Handle refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchAllSessions();
    } finally {
      setRefreshing(false);
    }
  };

  // Handle join/continue quiz
  const handleJoinQuiz = async (session) => {
    // Check if user is authenticated (registered or guest)
    if (!isAuthenticated || !user) {
      setAuthModalOpen(true);
      return;
    }

    // Only allow registered and guest users
    if (user.user_type !== 'registered' && user.user_type !== 'guest') {
      enqueueSnackbar('Please register or login as guest to join live quiz', { variant: 'warning' });
      setAuthModalOpen(true);
      return;
    }

    try {
      // Generate or get device ID
      let deviceId = localStorage.getItem('device_id');
      if (!deviceId) {
        deviceId = 'device_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        localStorage.setItem('device_id', deviceId);
      }
      
      const participantData = {
        device_id: deviceId,
        anonymous_id: `anon_${Date.now()}`,
        guest_name: user.user_type === 'guest' ? getGuestDisplayName(user) : null
      };

      const result = await joinSession(session.session_id, participantData);
      // Persist live session context for fallback on results page
      try {
        localStorage.setItem('live_session_id', String(session.session_id));
        if (result?.participant_id) {
          localStorage.setItem('live_participant_id', String(result.participant_id));
        }
      } catch (_) {}
      
      // Update participation status
      setUserParticipations(prev => ({
        ...prev,
        [session.session_id]: true
      }));
      
      // Show appropriate message
      if (result.is_rejoin) {
        enqueueSnackbar('Welcome back! Continuing your quiz session.', { variant: 'success' });
      } else {
        enqueueSnackbar('Successfully joined live quiz session!', { variant: 'success' });
      }
      
      if (onJoinQuiz) {
        onJoinQuiz(session, result);
      }
    } catch (err) {
      console.error('Join quiz error:', err);
      enqueueSnackbar('Failed to join live quiz session', { variant: 'error' });
    }
  };

  // Format duration
  const formatDuration = (minutes) => {
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  };

  if (loading) {
    return (
      <WelcomeContainer>
        <AppBar position="static" sx={{ bgcolor: 'background.paper', boxShadow: 1, mb: 3 }}>
          <Toolbar>
            <IconButton onClick={onBack} sx={{ mr: 2 }}>
              <BackIcon />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Live Quiz Sessions
            </Typography>
            <UserAuthButton />
          </Toolbar>
        </AppBar>
        
        <Container maxWidth="lg">
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress sx={{ color: '#4CAF50' }} />
            <Typography variant="h6" sx={{ ml: 2, color: '#fff' }}>
              Loading live quiz sessions...
            </Typography>
          </Box>
        </Container>
      </WelcomeContainer>
    );
  }

  return (
    <WelcomeContainer>
      <AppBar position="static" sx={{ bgcolor: 'background.paper', boxShadow: 1, mb: 3 }}>
        <Toolbar>
          <IconButton onClick={onBack} sx={{ mr: 2 }}>
            <BackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Live Quiz Sessions
          </Typography>
          <IconButton onClick={handleRefresh} disabled={refreshing}>
            <RefreshIcon />
          </IconButton>
          <UserAuthButton />
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg">
        {/* Header */}
        <Box textAlign="center" mb={4}>
          <LiveIcon sx={{ fontSize: 80, color: '#4CAF50', mb: 2 }} />
          <Typography variant="h3" sx={{ color: '#fff', fontWeight: 700, mb: 2 }}>
            Live Quiz Sessions
          </Typography>
          <Typography variant="h6" sx={{ color: '#ccc', fontWeight: 400 }}>
            Join real-time multiplayer quiz sessions and compete with other players
          </Typography>
        </Box>

        {/* Error Alert */}
        {(error || allSessionsError) && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error || allSessionsError}
          </Alert>
        )}

        {/* Loading State */}
        {(loading || allSessionsLoading) && (
          <Box display="flex" justifyContent="center" alignItems="center" py={8}>
            <CircularProgress size={60} sx={{ color: '#4CAF50' }} />
            <Typography variant="h6" sx={{ color: '#ccc', ml: 2 }}>
              Loading quiz sessions...
            </Typography>
          </Box>
        )}

        {/* All Sessions */}
        {!loading && !allSessionsLoading && allSessions.length === 0 ? (
          <StyledCard>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <LiveIcon sx={{ fontSize: 64, color: '#666', mb: 2 }} />
              <Typography variant="h5" sx={{ color: '#ccc', mb: 2 }}>
                No Quiz Sessions Available
              </Typography>
              <Typography variant="body1" sx={{ color: '#666', mb: 3 }}>
                There are currently no quiz sessions available. Check back later or ask an admin to create one.
              </Typography>
              <ActionButton onClick={handleRefresh} status="active">
                Refresh Sessions
              </ActionButton>
            </CardContent>
          </StyledCard>
        ) : (
          <>
            {/* Stats */}
            <Grid container spacing={3} mb={4}>
              <Grid item xs={12} sm={4}>
                <StyledCard>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <LiveIcon sx={{ fontSize: 40, color: '#4CAF50', mb: 1 }} />
                    <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700 }}>
                      {activeSessions.length}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#ccc' }}>
                      Active Sessions
                    </Typography>
                  </CardContent>
                </StyledCard>
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <StyledCard>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <PeopleIcon sx={{ fontSize: 40, color: '#2196F3', mb: 1 }} />
                    <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700 }}>
                      {activeSessions.reduce((sum, s) => sum + (s.participant_count || 0), 0)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#ccc' }}>
                      Total Participants
                    </Typography>
                  </CardContent>
                </StyledCard>
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <StyledCard>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <TrophyIcon sx={{ fontSize: 40, color: '#FF9800', mb: 1 }} />
                    <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700 }}>
                      Live
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#ccc' }}>
                      Competition Mode
                    </Typography>
                  </CardContent>
                </StyledCard>
              </Grid>
            </Grid>

            {/* Sessions Grid */}
            <Typography variant="h5" sx={{ color: '#fff', mb: 3, fontWeight: 600 }}>
              Quiz Sessions
            </Typography>
            
            <Grid container spacing={3}>
              {allSessions.map((session) => {
                const config = typeof session.quiz_config === 'string' 
                  ? JSON.parse(session.quiz_config) 
                  : session.quiz_config;
                
                return (
                  <Grid item xs={12} md={6} lg={4} key={session.session_id}>
                    <StyledCard>
                      <CardContent>
                        {/* Session Header */}
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                          <Box flex={1}>
                            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, mb: 1 }}>
                              {session.quiz_name}
                            </Typography>
                            <StatusChip 
                              label={session.computedStatus === 'active' ? 'LIVE' : 
                                     session.computedStatus === 'upcoming' ? 'UPCOMING' : 'EXPIRED'} 
                              status={session.computedStatus}
                              size="small"
                            />
                          </Box>
                          {session.computedStatus === 'active' ? (
                            <Badge badgeContent="LIVE" color="error">
                              <LiveIcon sx={{ color: '#f44336' }} />
                            </Badge>
                          ) : session.computedStatus === 'upcoming' ? (
                            <Badge badgeContent="UPCOMING" color="primary">
                              <UpcomingIcon sx={{ color: '#2196F3' }} />
                            </Badge>
                          ) : (
                            <Badge badgeContent="EXPIRED" color="default">
                              <TimerIcon sx={{ color: '#9E9E9E' }} />
                            </Badge>
                          )}
                        </Box>

                        {/* Session Info */}
                        <Box mb={3}>
                          <Box display="flex" alignItems="center" mb={1}>
                            <TimerIcon sx={{ fontSize: 16, color: '#ccc', mr: 1 }} />
                            <Typography variant="body2" sx={{ color: '#ccc' }}>
                              {formatDuration(config.duration || 5)}
                            </Typography>
                          </Box>
                          
                          <Box display="flex" alignItems="center" mb={1}>
                            <PeopleIcon sx={{ fontSize: 16, color: '#ccc', mr: 1 }} />
                            <Typography variant="body2" sx={{ color: '#ccc' }}>
                              {session.participant_count || 0} players joined
                            </Typography>
                          </Box>
                          
                          {session.scheduled_start_time && (
                            <Box display="flex" alignItems="center" mb={1}>
                              <ScheduleIcon sx={{ fontSize: 16, color: '#ccc', mr: 1 }} />
                              <Typography variant="body2" sx={{ color: '#ccc' }}>
                                {session.computedStatus === 'upcoming' 
                                  ? `Starts in ${getTimeUntilStart(session.scheduled_start_time)}`
                                  : session.computedStatus === 'active'
                                    ? 'Started'
                                    : 'Expired'}
                              </Typography>
                            </Box>
                          )}
                        </Box>

                        {/* Difficulty Indicator */}
                        <Box mb={3}>
                          <Typography variant="body2" sx={{ color: '#ccc', mb: 1 }}>
                            Difficulty: {config.difficulty?.charAt(0).toUpperCase() + config.difficulty?.slice(1) || 'Medium'}
                          </Typography>
                          <LinearProgress 
                            variant="determinate" 
                            value={
                              config.difficulty === 'easy' ? 25 : 
                              config.difficulty === 'medium' ? 50 : 
                              config.difficulty === 'hard' ? 75 : 100
                            }
                            sx={{ 
                              backgroundColor: '#333',
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: 
                                  config.difficulty === 'easy' ? '#4CAF50' : 
                                  config.difficulty === 'medium' ? '#FF9800' : 
                                  config.difficulty === 'hard' ? '#f44336' : '#9C27B0'
                              }
                            }} 
                          />
                        </Box>

                        {/* Leaderboard Section */}
                        <Box mb={3}>
                          <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<LeaderboardIcon />}
                            endIcon={expandedLeaderboards[session.session_id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            onClick={() => toggleLeaderboard(session.session_id)}
                            sx={{
                              borderColor: '#333',
                              color: '#ccc',
                              textTransform: 'none',
                              justifyContent: 'space-between',
                              py: 1,
                              '&:hover': {
                                borderColor: '#555',
                                backgroundColor: '#222'
                              }
                            }}
                          >
                            Leaderboard ({session.participant_count || 0})
                          </Button>
                          
                          <Collapse in={expandedLeaderboards[session.session_id]}>
                            <Box mt={2}>
                              {leaderboardLoading[session.session_id] ? (
                                <Box display="flex" justifyContent="center" py={2}>
                                  <CircularProgress size={24} sx={{ color: '#4CAF50' }} />
                                </Box>
                              ) : (
                                <Card variant="outlined" sx={{ backgroundColor: '#0F0F0F', border: '1px solid #333' }}>
                                  <CardContent sx={{ p: 2 }}>
                                    {leaderboards[session.session_id]?.length > 0 ? (
                                      <List dense>
                                        {leaderboards[session.session_id].slice(0, 5).map((participant, index) => (
                                          <ListItem key={participant.participant_id} sx={{ px: 0 }}>
                                            <ListItemAvatar>
                                              <Avatar sx={{ 
                                                bgcolor: index === 0 ? '#FFD700' : 
                                                        index === 1 ? '#C0C0C0' : 
                                                        index === 2 ? '#CD7F32' : '#4CAF50',
                                                width: 32,
                                                height: 32,
                                                fontSize: '0.8rem',
                                                fontWeight: 'bold'
                                              }}>
                                                #{index + 1}
                                              </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText
                                              primary={
                                                <Typography variant="body2" sx={{ color: '#fff', fontWeight: 500 }}>
                                                  {(participant.fname && participant.lname) ? `${participant.fname} ${participant.lname}` : 
                                                   participant.guest_name || 
                                                   participant.anonymous_id || 
                                                   'Anonymous'}
                                                </Typography>
                                              }
                                              secondary={
                                                <Typography variant="caption" sx={{ color: '#999' }}>
                                                  {participant.current_score || 0} points
                                                </Typography>
                                              }
                                            />
                                          </ListItem>
                                        ))}
                                        {leaderboards[session.session_id].length > 5 && (
                                          <ListItem sx={{ px: 0 }}>
                                            <ListItemText
                                              primary={
                                                <Typography variant="caption" sx={{ color: '#666', textAlign: 'center' }}>
                                                  ... and {leaderboards[session.session_id].length - 5} more
                                                </Typography>
                                              }
                                            />
                                          </ListItem>
                                        )}
                                      </List>
                                    ) : (
                                      <Typography variant="body2" sx={{ color: '#666', textAlign: 'center', py: 2 }}>
                                        No participants yet
                                      </Typography>
                                    )}
                                  </CardContent>
                                </Card>
                              )}
                            </Box>
                          </Collapse>
                        </Box>

                        {/* Join/Continue Button */}
                        <ActionButton
                          fullWidth
                          startIcon={<PlayIcon />}
                          onClick={() => session.computedStatus === 'active' ? handleJoinQuiz(session) : null}
                          disabled={session.computedStatus !== 'active' || userParticipations[session.session_id] === 'completed'}
                          status={session.computedStatus}
                          sx={{ py: 1.5 }}
                        >
                          {session.computedStatus === 'upcoming' 
                            ? 'Upcoming' 
                            : session.computedStatus !== 'active' 
                              ? 'Expired' 
                              : userParticipations[session.session_id] === 'completed' 
                                ? 'Completed' 
                                : (userParticipations[session.session_id] ? 'Continue Quiz' : 'Join Live Quiz')}
                        </ActionButton>
                      </CardContent>
                    </StyledCard>
                  </Grid>
                );
              })}
            </Grid>
          </>
        )}
      </Container>

      {/* Auth Modal */}
      <Dialog
        open={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#1A1A1A',
            border: '1px solid #333',
            borderRadius: '16px'
          }
        }}
      >
        <DialogTitle sx={{ color: '#fff', borderBottom: '1px solid #333', textAlign: 'center' }}>
          <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
            <LockIcon sx={{ fontSize: 32, color: '#4CAF50', mr: 1 }} />
            <Typography variant="h5" fontWeight="bold">
              Authentication Required
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box textAlign="center">
            <Typography variant="body1" sx={{ color: '#ccc', mb: 3 }}>
              To join live quiz sessions, you need to be either a registered user or a guest user.
            </Typography>
            <Typography variant="body2" sx={{ color: '#999', mb: 3 }}>
              Please choose one of the options below to continue:
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #333', p: 3, justifyContent: 'center' }}>
          <AuthModal 
            trigger={
              <Button
                variant="contained"
                sx={{
                  backgroundColor: '#4CAF50',
                  color: '#fff',
                  px: 4,
                  py: 1.5,
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: '#45a049'
                  }
                }}
              >
                Login / Register
              </Button>
            }
            onClose={() => setAuthModalOpen(false)}
          />
          <Button
            onClick={() => setAuthModalOpen(false)}
            sx={{
              color: '#ccc',
              px: 4,
              py: 1.5,
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: '1rem',
              border: '1px solid #333',
              '&:hover': {
                borderColor: '#555',
                backgroundColor: '#222'
              }
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </WelcomeContainer>
  );
};

export default LiveQuizWelcome;
